import type React from 'react';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Text } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { PAYMENT_METHOD } from '../../../../constants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  onAddOne: () => void;
  onRemoveAll: () => void;
};

export default function PaymentMethodListBox({ section, setSectionValues, onRemoveAll, onAddOne }: Props): JSX.Element {
  function onOptionChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value, checked } = event.currentTarget;

    if (value === PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED) {
      if (checked) {
        onAddOne();
      } else {
        onRemoveAll();
      }
    }

    setSectionValues({
      [value]: checked,
    });
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  const zillowPayments = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED);
  const checkOrMoneyOrder = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);
  const directDeposit = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED);
  const otherElectronicPayment = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);
  const isCheckOrMoneyOrderDisabled = section.isFieldDisabled(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);

  return (
    <FieldSet required legend={<Legend>Which forms of payment will you accept?</Legend>}>
      <LabeledControl
        label={
          <Label>
            Zillow Payments
            <br />
            <Text css={{ color: 'text.subtle' }}>
              Tenant pays through our online service. Set up quickly using information from your lease.
            </Text>
          </Label>
        }
        control={
          <Checkbox
            value={PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED}
            checked={zillowPayments}
            onChange={onOptionChange}
          />
        }
      />
      <LabeledControl
        disabled={isCheckOrMoneyOrderDisabled}
        label={
          <Label>
            Check or money order
            <br />
            <Text css={{ color: 'text.subtle' }}>
              Tenant sends payment to either the landlord or the property manager.
            </Text>
          </Label>
        }
        control={
          <Checkbox
            value={PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED}
            checked={checkOrMoneyOrder}
            onChange={onOptionChange}
          />
        }
      />
      <LabeledControl
        label={
          <Label>
            Direct deposit
            <br />
            <Text css={{ color: 'text.subtle' }}>
              Tenant transfers funds using bank info, including ACH, if you provide them.
            </Text>
          </Label>
        }
        control={
          <Checkbox
            value={PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED}
            checked={directDeposit}
            onChange={onOptionChange}
          />
        }
      />
      <LabeledControl
        label={
          <Label>
            Other electronic payment
            <br />
            <Text css={{ color: 'text.subtle' }}>Tenant pays via third party online service of your choice.</Text>
          </Label>
        }
        control={
          <Checkbox
            value={PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED}
            checked={otherElectronicPayment}
            onChange={onOptionChange}
          />
        }
      />
    </FieldSet>
  );
}
